import { http, HttpResponse, delay } from 'msw';

import { applicationsList } from '@/mocks/data/applicationData';
import {
  appChampionsByEmployeeId,
  createOrganizationApplication,
  organizationApplicationsList,
} from '@/mocks/data/organizationApplicationData';
import { isApplicationMockConnected } from '@/mocks/data/mockConnectedIntegrations';

// The mock OAuth callback (integrationHandlers.ts) can't mutate
// organizationApplicationsList directly -- it runs in the popup's own
// separate window/MSW instance, so an in-memory change there wouldn't be
// visible here. It persists via localStorage instead; this applies that on
// top of the static seed data whenever an org application is read.
const withMockConnectedOverride = (
  orgApp: (typeof organizationApplicationsList)[number]
): (typeof organizationApplicationsList)[number] =>
  isApplicationMockConnected(orgApp.application.slug, orgApp.application.id)
    ? { ...orgApp, integration_connected: true }
    : orgApp;

export const organizationApplicationHandlers = [
  http.get(
    `*/v1/organizations/:id/organization_applications`,
    async ({ request }) => {
      const url = new URL(request.url);
      let limit = 10;
      const limitParam = url.searchParams.get('limit');
      if (limitParam) {
        limit = parseInt(limitParam);
      }
      const offsetParam = url.searchParams.get('offset');
      const championEmployeeId = url.searchParams.get('champion_employee_id');

      let filteredList = organizationApplicationsList;
      if (championEmployeeId) {
        const championedSlugs =
          appChampionsByEmployeeId[championEmployeeId] ?? [];
        filteredList = organizationApplicationsList.filter(orgApp =>
          championedSlugs.includes(orgApp.application.slug)
        );
      }

      let organizationApplicationsListPage = filteredList.slice(0, limit);
      if (offsetParam) {
        const offset = parseInt(offsetParam);
        if (offset > 0) {
          organizationApplicationsListPage = filteredList.slice(
            offset,
            offset + limit
          );
        }
      }
      await delay(200);
      return HttpResponse.json(
        {
          total: filteredList.length,
          results: organizationApplicationsListPage.map(
            withMockConnectedOverride
          ),
        },
        { status: 200 }
      );
    }
  ),
  http.get(
    `*/v1/organizations/:id/organization_applications/:applicationKey`,
    async ({ params }) => {
      const { applicationKey } = params;

      // The slice's transformResponse expects an OrganizationApplicationDto
      // (with a nested `application` field), so prefer the org-app list first.
      // If only a raw Application matches, wrap it into a minimal org-app
      // envelope so transformApplication doesn't blow up reading `.application.id`.
      const orgApp = organizationApplicationsList.find(
        oa =>
          oa.id === applicationKey ||
          oa.application.slug === applicationKey ||
          oa.application.id === applicationKey
      );

      await delay(200);
      if (orgApp) {
        return HttpResponse.json(withMockConnectedOverride(orgApp), {
          status: 200,
        });
      }

      const rawApp = applicationsList.find(
        app => app.slug === applicationKey || app.id === applicationKey
      );
      if (rawApp) {
        return HttpResponse.json(
          { ...createOrganizationApplication, application: rawApp },
          { status: 200 }
        );
      }

      return HttpResponse.json({}, { status: 404 });
    }
  ),
  http.post(
    `*/v1/organizations/:id/organization_applications`,
    async ({ request }) => {
      const body = (await request.json()) as { application_id?: string };
      const app = applicationsList.find(app => app.id === body?.application_id);
      const orgApp = {
        ...createOrganizationApplication,
        application: { ...createOrganizationApplication.application, ...app },
      };

      organizationApplicationsList.push(orgApp);

      await delay(200);
      return HttpResponse.json(createOrganizationApplication, { status: 201 });
    }
  ),
  // Powers the Add Applications footer submit (useAddOrganizationApplicationsBulkMutation).
  http.post(
    `*/v1/organizations/:id/organization_applications/bulk`,
    async ({ request }) => {
      const body = (await request.json()) as {
        applications?: { application_id: string }[];
      };
      const orgApplications = (body.applications ?? []).map(
        ({ application_id: applicationId }) => {
          const app = applicationsList.find(app => app.id === applicationId);
          const orgApp = {
            ...createOrganizationApplication,
            id: `${createOrganizationApplication.id}-${applicationId}`,
            application: {
              ...createOrganizationApplication.application,
              ...app,
            },
            // Notion mocks as already connected (so the Configure table's
            // "Connected" state + enabled Automated option are visible
            // without a real OAuth/Nango round-trip, which mocks can't
            // complete) — GitHub/Linear stay disconnected to show "Connect".
            integration_connected: app?.slug === 'notion',
            onboarding_automation_on: app?.slug === 'notion',
            offboarding_automation_on: app?.slug === 'notion',
          };
          organizationApplicationsList.push(orgApp);
          return orgApp;
        }
      );

      await delay(200);
      return HttpResponse.json(
        { organization_applications: orgApplications },
        { status: 201 }
      );
    }
  ),
  // Powers the Configure step's onboarding/offboarding automation selects
  // (useUpdateOrganizationApplicationMutation).
  http.put(
    `*/v1/organizations/:organizationId/organization_applications/:applicationKey`,
    async ({ params, request }) => {
      const { applicationKey } = params;
      const body = (await request.json()) as {
        onboarding_automation_on?: boolean;
        offboarding_automation_on?: boolean;
        disable_create_account_requests?: boolean;
        disable_delete_account_requests?: boolean;
      };
      const orgApp = organizationApplicationsList.find(
        oa =>
          oa.application.id === applicationKey ||
          oa.application.slug === applicationKey
      );
      if (orgApp) {
        Object.assign(orgApp, {
          onboarding_automation_on: body.onboarding_automation_on,
          offboarding_automation_on: body.offboarding_automation_on,
          disable_create_account_requests: body.disable_create_account_requests,
          disable_delete_account_requests: body.disable_delete_account_requests,
        });
      }
      await delay(200);
      return HttpResponse.json({}, { status: 200 });
    }
  ),
  // Powers the Configure step's Primary champion select
  // (useUpdateOrganizationApplicationChampionsMutation).
  http.put(
    `*/v1/organizations/:organizationId/organization_applications/:applicationKey/champions`,
    async () => {
      await delay(200);
      return HttpResponse.json({}, { status: 200 });
    }
  ),
  http.delete(
    `*/v1/organizations/:organizationId/organization_applications/:applicationKey`,
    async ({ params }) => {
      const { applicationKey } = params;
      organizationApplicationsList.splice(
        organizationApplicationsList.findIndex(
          a =>
            a.application.id === applicationKey ||
            a.application.slug === applicationKey
        ),
        1
      );
      await delay(200);
      return new HttpResponse(null, { status: 204 });
    }
  ),
];
